import type { ImageDerivativeResponse, ImageUsageResponse } from '../dto/media'
import type { ImageCropMode } from '../enums'

/**
 * What a surface needs to render one image for one usage.
 *
 * Built from the same rows the derivatives were rendered from, so the widths
 * a `srcset` advertises are the widths of the files behind it. A hand-written
 * srcset is where those two drift, and the browser picks a file that is not
 * the size it was told.
 */
export type ImageSource = {
  /** `<source type="image/avif">` and its WebP sibling, in that order. */
  sources: Array<{ mimeType: string; srcSet: string }>
  /** The `<img src>` a browser without `<picture>` falls back to. */
  fallback: string
  /** What `sizes` should say, from the usage's own target width. */
  sizes: string
  width: number
  height: number | null
}

const MIME_TYPES = { AVIF: 'image/avif', WEBP: 'image/webp' } as const

/**
 * Builds the sources for one usage out of an asset's rendered derivatives.
 *
 * Returns null where the asset has none yet — a library being reprocessed in
 * the background has assets in both states, and the caller falls back to the
 * old size scheme rather than rendering nothing.
 */
export function buildImageSource(
  baseUrl: string,
  usage: Pick<
    ImageUsageResponse,
    'key' | 'targetWidth' | 'aspectWidth' | 'aspectHeight'
  >,
  derivatives: ImageDerivativeResponse[],
): ImageSource | null {
  const forUsage = derivatives.filter(item => item.usageKey === usage.key)
  if (forUsage.length === 0) return null

  const sources = (['AVIF', 'WEBP'] as const)
    .map(format => {
      const entries = forUsage
        .filter(item => item.format === format)
        .sort((a, b) => a.density - b.density)
      if (entries.length === 0) return null
      return {
        mimeType: MIME_TYPES[format],
        // Described by width rather than by `2x`: a `w` descriptor lets the
        // browser account for the layout width too, which a density
        // descriptor cannot.
        srcSet: entries
          .map(item => `${baseUrl}/${item.objectKey} ${item.width}w`)
          .join(', '),
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)

  if (sources.length === 0) return null

  const fallbackEntry =
    forUsage
      .filter(item => item.format === 'WEBP')
      .sort((a, b) => a.density - b.density)[0] ?? forUsage[0]

  return {
    sources,
    fallback: `${baseUrl}/${fallbackEntry.objectKey}`,
    sizes: `${usage.targetWidth}px`,
    ...targetSize(usage, 1),
  }
}

/**
 * The pixel size a usage wants at one density.
 *
 * Null height means the usage has no aspect ratio and keeps the source's —
 * the width is the only constraint. Shared with the renderer so the `srcset`
 * advertises the size of the file actually behind it.
 */
export function targetSize(
  usage: Pick<
    ImageUsageResponse,
    'targetWidth' | 'aspectWidth' | 'aspectHeight'
  >,
  density: number,
): { width: number; height: number | null } {
  const width = usage.targetWidth * density
  if (!usage.aspectWidth || !usage.aspectHeight) {
    return { width, height: null }
  }
  return {
    width,
    height: Math.round((width * usage.aspectHeight) / usage.aspectWidth),
  }
}

/**
 * The part of a usage that decides pixel sizes. Stated structurally so a
 * caller holding a projection of the row can ask the same questions.
 */
export type UsageGeometry = Pick<
  ImageUsageResponse,
  'targetWidth' | 'aspectWidth' | 'aspectHeight' | 'densities'
>

/**
 * The smallest original that can serve this usage without being enlarged.
 *
 * The highest density decides it: an image that only satisfies 1× is upscaled
 * for the 3× file, which is the blur nobody can explain afterwards.
 */
export function minimumSourceSize(usage: UsageGeometry): {
  width: number
  height: number | null
} {
  const highest = usage.densities.length ? Math.max(...usage.densities) : 1
  return targetSize(usage, highest)
}

/** Whether an original is large enough for a usage, and by how much it misses. */
export function checkResolution(
  usage: UsageGeometry,
  source: { width: number | null; height: number | null },
): {
  sufficient: boolean
  requiredWidth: number
  requiredHeight: number | null
} {
  const required = minimumSourceSize(usage)
  const sufficient =
    (source.width ?? 0) >= required.width &&
    (required.height === null || (source.height ?? 0) >= required.height)

  return {
    sufficient,
    requiredWidth: required.width,
    requiredHeight: required.height,
  }
}

/** The middle of the picture, where a usage crops without following a point. */
const CENTRE_FOCAL = { x: 0.5, y: 0.5 }

type CropShape = {
  aspectWidth: number | null
  aspectHeight: number | null
  cropMode: ImageCropMode
}

/**
 * Whether a usage cuts the picture, or keeps the whole frame.
 *
 * One answer for the renderer, the delivery redirect and the editor that draws
 * on the picture: a usage with an aspect is resized `cover`, so its file is a
 * slice, and a fraction of that slice is not the fraction anyone means.
 * `CONTAIN` fits the picture inside the box and keeps all of it.
 */
export function usageCrops<T extends CropShape>(
  usage: T,
): usage is T & { aspectWidth: number; aspectHeight: number } {
  return (
    !!usage.aspectWidth && !!usage.aspectHeight && usage.cropMode !== 'CONTAIN'
  )
}

/**
 * The usage to draw an uncut copy of the picture from, for a box of a given
 * width.
 *
 * Only a usage that keeps the whole frame will do: a surface that points at
 * image coordinates — a focal point, a crop — needs its own percentages to be
 * the picture's, and a fraction of a cut file is not that. Among those, the
 * narrowest that still covers the box; the widest is a hero-sized file to
 * paint a preview and a row of thumbnails with. The widest is what is left
 * where nothing reaches the width.
 */
export function uncroppedUsageFor<
  T extends CropShape & { targetWidth: number },
>(usages: T[], minimumWidth: number): T | undefined {
  const whole = usages
    .filter(usage => !usageCrops(usage))
    .sort((a, b) => a.targetWidth - b.targetWidth)
  return whole.find(usage => usage.targetWidth >= minimumWidth) ?? whole.at(-1)
}

/**
 * The point a usage cuts around. Only `FOCAL_POINT` follows the operator's;
 * the rest keep the middle, so a preview that moved with the dot for them
 * would promise a file the renderer never writes.
 */
export function focalFor(
  usage: Pick<CropShape, 'cropMode'>,
  focal: { x: number; y: number },
): { x: number; y: number } {
  return usage.cropMode === 'FOCAL_POINT' ? focal : CENTRE_FOCAL
}
